import type { Decimal } from './decimal'

/** "2026-09" */
export type MonthKey = string

export type OpportunityStatus = 'ACTIVE' | 'WON' | 'LOST'
export type OpportunityStage = 'LIKELY' | 'POSSIBLE' | 'SPECULATIVE'

/**
 * De lagen waarlangs je kiest wat er meetelt. Gewonnen werk is strikt genomen een
 * status en geen trap, maar in het scherm kies je precies langs deze vier lijnen.
 */
export type PlanningStage = 'WON' | OpportunityStage
export type OpportunityRevenueModel = 'HOURLY' | 'FIXED'
export type CapacitySource = 'manual' | 'contract' | 'none'

/** De banden waarin de uren getekend worden, in stapelvolgorde. */
export const STAGE_BANDS = ['won', 'likely', 'possible', 'speculative', 'undistributed'] as const

export type StageBand = (typeof STAGE_BANDS)[number]

/**
 * Twee indelingen van hetzelfde totaal. `committedHours`/`uncertainHours` zegt
 * hoe hard het werk is; `byStage` is de tekening. Ze sommeren allebei op
 * `plannedHours`, en daar hangen vrij en overboekt aan: alles wat meetelt drukt
 * op de maand.
 */
export interface PlanningCell {
  month: MonthKey
  committedHours: Decimal
  uncertainHours: Decimal
  byStage: Record<StageBand, Decimal>
  plannedHours: Decimal
  internalHours: Decimal
  /** Null als er geen contracturen zijn en er ook niets handmatig staat. */
  capacityHours: Decimal | null
  capacitySource: CapacitySource
  /** Waar een afgeleid getal vandaan komt; null bij handmatig of zonder basis. */
  capacityBasis: { workdays: number; vacationDays: number; holidayDays: number } | null
  /** Capaciteit min alles wat meetelt; negatief betekent overbezet. Null zonder capaciteit. */
  freeHours: Decimal | null
  overbooked: boolean
  /** De ideale bezetting in uren; zonder afgesproken percentage de hele capaciteit, null zonder capaciteit. */
  targetHours: Decimal | null
  /** Geplande uren boven het streven. Staat altijd aan zodra `overbooked` aanstaat. */
  overTarget: boolean
  /**
   * Wat deze uren opbrengen. Op een persoonsregel alleen het werk dat aan hem
   * hangt; een vaste prijs hangt aan de kans en staat daarom alleen op de maand,
   * dus de persoonsregels sommeren niet per se op het maandtotaal.
   */
  revenue: Decimal
}

export interface TeamMonth extends PlanningCell {
  unassignedHours: Decimal
}

export interface PersonRow {
  personId: string
  displayName: string
  /** Ideale bezetting in procent van de beschikbare uren; null = de standaard van 100. */
  planningTargetPercent: number | null
  months: PlanningCell[]
}

export interface OpportunityAllocation {
  id: string
  personId: string | null
  personName: string | null
  month: MonthKey
  hours: Decimal
  inWindow: boolean
  /** Wat deze regel opbrengt; null zodra er geen bedrag aan te hangen is. */
  amount: Decimal | null
}

export interface OpportunityRevenueRow {
  id: string
  month: MonthKey
  amount: Decimal
}

/** Een korte zin die om een besluit vraagt, geen foutmelding. */
export interface OpportunityNotice {
  key:
    | 'missingRate'
    | 'missingAmount'
    | 'wonWithoutProject'
    | 'archivedClient'
    | 'expired'
    | 'unassignedHours'
    | 'undistributed'
    | 'overDistributed'
  text: string
}

export interface Opportunity {
  id: string
  name: string
  status: OpportunityStatus
  statusLabel: string
  stage: OpportunityStage
  stageLabel: string
  /** Alleen om op te sorteren; de omzet wordt er niet mee afgewaardeerd. */
  weightPercentage: number
  revenueModel: OpportunityRevenueModel
  hourlyRateOverride: Decimal | null
  /** De omvang van de klus, los van de verdeling over personen en maanden. */
  totalHours: Decimal | null
  /** De zelf gesleepte plek in de lijst; leeg zolang er niets versleept is. */
  position: number | null
  /** Over alle maanden geteld, niet alleen het venster. */
  distribution: { total: Decimal | null; distributed: Decimal; undistributed: Decimal | null }
  notes: string | null
  client: { id: string; name: string; internal: boolean; archived: boolean } | null
  project: { id: string; name: string; lead: { id: string; displayName: string } | null } | null
  /** Alle maandregels, ook buiten het venster: anders kun je werk niet verschuiven. */
  allocations: OpportunityAllocation[]
  revenues: OpportunityRevenueRow[]
  monthlyRevenue: { month: MonthKey; amount: Decimal }[]
  span: { first: MonthKey | null; last: MonthKey | null }
  /** Binnen het getoonde venster. */
  totals: { hours: Decimal; revenue: Decimal }
  outsideWindow: { hours: Decimal; months: number }
  notices: OpportunityNotice[]
}

export interface PlanningOverview {
  from: MonthKey
  months: MonthKey[]
  /** De toegepaste keuze, terug uit de server: alles hieronder is erop gefilterd. */
  stages: PlanningStage[]
  team: TeamMonth[]
  persons: PersonRow[]
  unassigned: { months: PlanningCell[] }
  /** Omvang die nog nergens staat, gelijk over het venster uitgesmeerd. */
  undistributed: { months: PlanningCell[] }
  opportunities: Opportunity[]
  /** De omzet van de trappen die meetellen, voor het volle bedrag. */
  revenue: Decimal
  /** Verloren kansen: uit beeld, maar terug te halen. */
  lost: { id: string; name: string; clientName: string | null }[]
}

export interface HistorySuggestionRow {
  personId: string
  personName: string
  clientId: string
  clientName: string
  internal: boolean
  month: MonthKey
  hours: Decimal
  opportunityId: string | null
  options: { id: string; name: string }[]
  proposedName: string
  /** Wat er voor deze persoon bij deze klant al staat; toepassen vervangt dat. */
  plannedHours: Decimal
}

export interface HistorySuggestion {
  month: MonthKey
  basedOn: { from: string; to: string; months: number }
  rows: HistorySuggestionRow[]
}

export interface OpportunityInput {
  name?: string
  clientId?: string | null
  projectId?: string | null
  stage?: OpportunityStage
  revenueModel?: OpportunityRevenueModel
  hourlyRateOverride?: string | null
  totalHours?: string | null
  notes?: string | null
}
